import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { sendAdvisorMessage } from '../../api/advisor';
import type { AdvisorCitation } from '../../api/advisor';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  citations: AdvisorCitation[];
}

const OPEN_KEY = 'advisor_chat_open';

export default function AdvisorChat() {
  const { user } = useAppSelector((state) => state.auth);
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(OPEN_KEY) === 'true'; } catch { return false; }
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Persist open/closed state
  useEffect(() => {
    try { localStorage.setItem(OPEN_KEY, String(isOpen)); } catch { /* noop */ }
  }, [isOpen]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading || !user?.id) return;

    const userText = input.trim();
    setInput('');

    const userMsg: Message = {
      id: `u-${Date.now()}`,
      role: 'user',
      text: userText,
      citations: [],
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const res = await sendAdvisorMessage(user.id, userText, sessionId);
      if (!sessionId) setSessionId(res.sessionId);

      const assistantMsg: Message = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        text: res.reply,
        citations: res.citations,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err: unknown) {
      const errorText =
        err instanceof Error && err.message.includes('403')
          ? 'This feature requires a Pro subscription. Upgrade to access the AI Advisor.'
          : 'Something went wrong. Please try again.';
      setMessages((prev) => [
        ...prev,
        { id: `err-${Date.now()}`, role: 'assistant', text: errorText, citations: [] },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, user?.id, sessionId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-accent px-4 py-3 rounded-full shadow-2xl text-white font-medium text-sm hover:bg-accent/90 transition-all focus:outline-none focus:ring-2 focus:ring-accent/50"
        aria-label="Open AI Advisor"
      >
        {/* Sparkle icon */}
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2zm0 10l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3z" />
        </svg>
        Ask AI
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-40 w-96 h-[520px] bg-surface rounded-2xl border border-border shadow-2xl flex flex-col overflow-hidden"
      role="dialog"
      aria-label="AI Portfolio Advisor"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-surface-hover border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-accent" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2zm0 10l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3z" />
          </svg>
          <span className="text-white font-semibold text-sm">AI Portfolio Advisor</span>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-text-muted hover:text-white transition-colors"
          aria-label="Close advisor"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Message thread */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-text-muted text-sm text-center mt-4 px-2">
            Ask me anything about your portfolio — risk, performance, taxes, or staking.
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className="max-w-[80%]">
              <div
                className={
                  msg.role === 'user'
                    ? 'bg-accent/10 text-white rounded-tl-2xl rounded-tr-sm rounded-bl-2xl px-3 py-2 text-sm'
                    : 'bg-surface-hover text-white rounded-tr-2xl rounded-tl-sm rounded-br-2xl px-3 py-2 text-sm'
                }
              >
                {msg.text}
              </div>
              {msg.citations.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {msg.citations.map((c) => (
                    <Link
                      key={c.url}
                      to={c.url}
                      className="text-xs text-accent underline hover:text-accent/80"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-surface-hover rounded-tr-2xl rounded-tl-sm rounded-br-2xl px-3 py-2">
              <div className="flex gap-1 items-center h-4">
                <span className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce [animation-delay:0ms]" />
                <span className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce [animation-delay:150ms]" />
                <span className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div className="px-4 py-3 border-t border-border shrink-0">
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your portfolio…"
            rows={1}
            className="flex-1 bg-surface-active text-white text-sm placeholder-text-muted rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-accent/50 min-h-[36px] max-h-24 overflow-y-auto"
            disabled={isLoading}
            maxLength={1000}
          />
          <button
            onClick={() => void handleSend()}
            disabled={isLoading || !input.trim()}
            className="shrink-0 bg-accent text-white rounded-xl px-3 py-2 text-sm font-medium hover:bg-accent/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all focus:outline-none focus:ring-2 focus:ring-accent/50"
            aria-label="Send message"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>
        <p className="text-xs text-text-muted mt-1.5">Powered by GPT-4o-mini · Informational only</p>
      </div>
    </div>
  );
}
